import { Done, WorkspacePremiumOutlined } from "@mui/icons-material";
import { Button, Progress, Skeleton, Typography } from "antd";
import React, { useState } from "react";
import CMEModal from "./CMEModal";

import { IDispatchSetStateAction } from "@Components/shared/types";
import {
    IQuestionnaireInstance,
    IQuestionnaireInstanceMinimal,
    IQuestionnaireInstanceMinimalSchema,
    IQuestionnaireInstanceSchema,
    IQuestionnaireMinimal,
} from "@Models/IQuestionnaireDetails";
import UserService from "@Services/UserService";
import { useSearchParams } from "@Utils";

const { Title, Text } = Typography;

interface IProps {
    isFetching: boolean;
    isLoadingCreateQuestionnaire: boolean;
    isCMEPublic: boolean;
    hasVideo: boolean;
    questionnaireInstanceData: IQuestionnaireInstance | IQuestionnaireMinimal;
    onAnswerQuestion: (questionId: string, choiceId: string) => Promise<void>;
    onComplete: () => Promise<string>;
    onGetStarted: (activityOwner: string) => Promise<void>;
    associatedStatus: { status: string };
    isAssociatedStatusHydrated: boolean;
}

/**
 * Type Predicate that checks if the data is a minimal questionnaire
 * @param data A questionnaire instance or a minimal questionnaire
 */
export function isQuestionnaireInstanceMinimal(
    data: IQuestionnaireInstance | IQuestionnaireInstanceMinimal
): data is IQuestionnaireInstanceMinimal {
    return IQuestionnaireInstanceMinimalSchema.safeParse(data).success;
}

/**
 * Calculate the progress of the questionnaire
 * @param questionnaireInstance The questionnaire instance
 */
const calculateValues = (questionnaireInstance: IQuestionnaireInstance) => {
    const values = { percentage: 0, progress: 0, total: 0 };

    const questions = questionnaireInstance?.questions ?? [];

    const answeredQuestions = questions?.filter((question) => !!question.answer) ?? [];
    values.progress = answeredQuestions?.length;
    values.total = questions.length;
    values.percentage = (values.progress / values.total) * 100;

    return values;
};

/**
 * CME Indicator component that shows the progress of the CME questionnaire
 */
const CMEIndicator = ({
    isFetching,
    isLoadingCreateQuestionnaire,
    isCMEPublic,
    hasVideo,
    questionnaireInstanceData,
    associatedStatus,
    isAssociatedStatusHydrated,
    onAnswerQuestion,
    onComplete,
    onGetStarted,
}: IProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showContinueAnimation, setShowContinueAnimation] = useState(false);
    const [showProgressAnimation, setShowProgressAnimation] = useState(false);

    const dataResult = IQuestionnaireInstanceSchema.safeParse(questionnaireInstanceData);
    const isNotStarted = !dataResult.success;

    const showModal = (currentProgress: number) => {
        setIsModalOpen(true);

        // only show the animations if the progress greater than 0
        if (currentProgress === 0) {
            return;
        }

        setShowContinueAnimation(true);
        setTimeout(() => {
            setShowContinueAnimation(false);
            setShowProgressAnimation(true);
        }, 2000);
        setTimeout(() => {
            setShowProgressAnimation(false);
        }, 4000);
    };

    const handleExit = () => {
        setIsModalOpen(false);
    };

    if ((isFetching || !isAssociatedStatusHydrated) && !isModalOpen) {
        return <Skeleton.Input active={true} block={true} size="large" />;
    }

    // we are checking associated status first as questionnaireInstance.completed on doesn't check for orphaned questionnaire instances
    // (i.e questionnaire instances where the questionnaire is now associated with a different activity type)
    if (associatedStatus?.status === "completed" && !isModalOpen) {
        return <CompletedIndicator />;
    }

    // If the questionnaire instance is not present, show the get started button
    if (isNotStarted) {
        return (
            <NotStartedIndicator
                isLoadingCreateQuestionnaire={isLoadingCreateQuestionnaire}
                onGetStarted={onGetStarted}
                setIsModalOpen={setIsModalOpen}
            />
        );
    }

    const questionnaireInstance = dataResult.data;

    const { percentage, progress, total } = calculateValues(questionnaireInstance);

    // if the questionnaire instance is completed
    if (questionnaireInstance.completedOn && !isModalOpen) {
        return <CompletedIndicator />;
    }

    // if the questionnaire instance is in progress
    return (
        <div className="CME-indicator-container incomplete flex">
            <div className="flex" style={{ gap: "10px" }}>
                <WorkspacePremiumOutlined />
                <div>
                    <div className="flex" style={{ gap: "10px" }}>
                        <Title level={5} style={{ marginBottom: "0px" }}>
                            CME Points
                        </Title>
                        <Text>Incomplete</Text>
                    </div>
                    <div className="flex" style={{ gap: "10px" }}>
                        <Progress percent={percentage} showInfo={false} />
                        <div>{`${progress}/${total}`}</div>
                    </div>
                </div>
            </div>
            <Button className="dark" onClick={() => showModal(progress)}>
                Continue
            </Button>
            {questionnaireInstance.questionnaireId ? (
                <CMEModal
                    handleExit={handleExit}
                    isModalOpen={isModalOpen}
                    isCMEPublic={isCMEPublic}
                    hasVideo={hasVideo}
                    onAnswerQuestion={onAnswerQuestion}
                    onComplete={onComplete}
                    questionnaireInstance={questionnaireInstance}
                    showContinueAnimation={showContinueAnimation}
                    showProgressAnimation={showProgressAnimation}
                />
            ) : null}
        </div>
    );
};

// ** Sub Components
const NotStartedIndicator = ({
    isLoadingCreateQuestionnaire,
    onGetStarted,
    setIsModalOpen,
}: {
    isLoadingCreateQuestionnaire: boolean;
    onGetStarted: (activityOwner: string) => Promise<void>;
    setIsModalOpen: IDispatchSetStateAction<boolean>;
}) => {
    const [searchParams] = useSearchParams();
    const urlUserId = searchParams.get("user");
    /**
     * Handle the get started button click
     */
    const handleGetStarted = async () => {
        const service = new UserService();
        const user = await service.getPublic([urlUserId]);

        if (!user.hasErrors) {
            onGetStarted(user.value[0].userGuid)
                .then(() => {
                    setIsModalOpen(true);
                })
                .catch(() => {
                    // handle error
                });
        }
    };

    return (
        <div className="CME-indicator-container incomplete flex">
            <div className="flex" style={{ gap: "10px" }}>
                <WorkspacePremiumOutlined />
                <Title level={5} style={{ marginBottom: "0px" }}>
                    Earn CME Points
                </Title>
            </div>
            <Button
                className="dark"
                disabled={isLoadingCreateQuestionnaire}
                loading={isLoadingCreateQuestionnaire}
                onClick={handleGetStarted}
            >
                Get started
            </Button>
        </div>
    );
};

const CompletedIndicator = () => (
    <div className="CME-indicator-container complete flex">
        <div className="done-icon-container">
            <Done style={{ fontSize: "18px" }} />
        </div>

        <Title level={5} style={{ marginBottom: "0px" }}>
            CME Points completed
        </Title>
    </div>
);

export default CMEIndicator;
